import { upperCaseFirst } from 'text-case'
import { any, propEq, reject, complement, both } from 'ramda'

/**
 * Build a formik error object based on known field-errors.
 *
 * Each fieldError should be an object with 3 properties:
 * - Field: the field name that the error belongs to
 * - Value: the value that the field had when the error ocurred
 * - Message: error message to be shown to the user
 * @param {[FieldError]} errors
 */
export const validateFieldErrors = errors => formValues => {
  return errors.reduce((err, { field, value, message }) => {
    if (value === formValues[field]) {
      // eslint-disable-next-line no-param-reassign
      err[field] = message
    }

    return err
  }, {})
}

// For each field with an error, store the field name and it's value.
// This will be used to validate fields on onChange/onBlur
export const formatErrors = (errors, formValues) => {
  return Object.keys(errors)
    .filter(field => field in formValues)
    .map(field => {
      return {
        field,
        value: formValues[field],
        message: errors[field].map(upperCaseFirst).join('. '),
      }
    })
}

/**
 * Remove all errors of dependent fields when a field changes
 *
 * Let's say that year field changes and month is a depentned field.
 * This function will remove all month errors when year changes.
 *
 * @param {*} dependencies - object where each key is a field name and each value is a dependent field name
 * @param {*} change - object that describe a change. It has two properties: name and value
 * @returns Function that receives an array of errors and returns new array of errors
 */
export const resetDependantFieldErrors = (dependencies, change) => errors => {
  const { name, value } = change
  const dependentField = dependencies[name]

  if (!dependentField) {
    return errors
  }

  const isDependentFieldError = propEq('field', dependentField)
  const fieldValueChanged = both(
    propEq('field', name),
    complement(propEq('value', value))
  )

  // Keep remoteErrors reference unchagned to avoid extra renderings
  if (any(fieldValueChanged, errors) && any(isDependentFieldError, errors)) {
    return reject(isDependentFieldError, errors)
  }

  return errors
}

export default validateFieldErrors
